/**
 * Trunnion AI - Agent Card
 * Summary card for an agent in the agent list view.
 */

import { Bot, Wrench, Shield, Zap, Eye } from 'lucide-react';
import { Card } from '@/components/ui/Card';
import { Badge } from '@/components/ui/Badge';
import { ClassificationTag } from '@/components/ui/ClassificationTag';
import { FavoriteButton } from '@/components/ui/FavoriteButton';
import type { AgentIdentity } from '@/types';

interface AgentCardProps {
  agent: AgentIdentity;
  onClick?: () => void;
}

const modeConfig: Record<string, { icon: typeof Zap; label: string; variant: 'warning' | 'info' | 'default' | 'success' }> = {
  autonomous: { icon: Zap, label: 'Autonomous', variant: 'warning' },
  supervised: { icon: Eye, label: 'Supervised', variant: 'info' },
  manual: { icon: Shield, label: 'Manual', variant: 'default' },
  reasoning: { icon: Zap, label: 'Reasoning', variant: 'info' },
  deterministic: { icon: Shield, label: 'Deterministic', variant: 'success' },
  console_automation: { icon: Wrench, label: 'Console Auto', variant: 'warning' },
};

const defaultMode = { icon: Bot, label: 'Unknown', variant: 'default' as const };

export function AgentCard({ agent, onClick }: AgentCardProps) {
  const mode = modeConfig[agent.mode] || defaultMode;
  const ModeIcon = mode.icon;

  return (
    <Card hover onClick={onClick} className="group">
      <div className="flex items-start gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-trunnion-gold-500/10 group-hover:bg-trunnion-gold-500/20 transition-colors">
          <Bot className="h-5 w-5 text-trunnion-gold-500" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold text-white truncate">
              {agent.name}
            </h3>
            <FavoriteButton type="agent" id={agent.id} label={agent.name} />
            {!agent.isActive && (
              <Badge variant="default">Inactive</Badge>
            )}
          </div>
          <p className="mt-0.5 text-xs text-trunnion-slate-400 line-clamp-2">
            {agent.description}
          </p>
          <div className="mt-2 flex items-center gap-3">
            <ClassificationTag level={agent.maxClassification} />
            <Badge variant={mode.variant}>
              <ModeIcon className="h-3 w-3 mr-1" />
              {mode.label}
            </Badge>
            <span className="flex items-center gap-1 text-xs text-trunnion-slate-400">
              <Wrench className="h-3 w-3" />
              {agent.toolWhitelist.length} tools
            </span>
            <span className="text-xs text-trunnion-slate-500 capitalize">
              {agent.tier}
            </span>
          </div>
        </div>
      </div>
    </Card>
  );
}
